/**
 * useQuery composable for data fetching with reactive state management.
 *
 * @param {Object} options - Options for the useQuery composable.
 * @param {import("vue").Ref<boolean>} [options.enabled=true] - Flag to enable or disable the initial fetch.
 * @param {Function} [options.queryFn=() => {}] - Function to fetch the data. Receives `params` as its argument.
 * @param {Function} [options.onSuccess=() => {}] - Callback function to be called on successful data fetch.
 * @param {Function} [options.onError=() => {}] - Callback function to be called on data fetch error.
 * @returns {Object} - Returns state properties and refetch function.
 */
const useQuery = ({ enabled = ref(true), queryFn = () => {}, onSuccess = () => {}, onError = () => {}, onPending = () => {}, onSettled = () => {} }) => {
	const state = reactive({
		data: {},
		error: {},
		status: null,
		isError: false,
		isSuccess: false,
		isPending: false,
	});

	const resetState = () => {
		state.data = {};
		state.error = {};
		state.status = null;
		state.isError = false;
		state.isSuccess = false;
		state.isPending = false;
	};

	/**
	 * Fetch data using the provided query function.
	 *
	 * @returns {Promise<void>} - A promise that resolves when the fetch operation is complete.
	 */
	const fetch = async () => {
		resetState();
		try {
			state.isPending = true;
			onPending();
			const response = await queryFn();
			if (!response) return;
			state.data = response.data;
			state.status = response.status;
			state.isSuccess = response.success;
			onSuccess(response.data, response.success, response.status);
		} catch (error) {
			state.error = error;
			state.isError = true;
			onError(error);
		} finally {
			state.isPending = false;
			onSettled(state.data, state.error);
		}
	};

	const rateLimit = computed(() => state.error?.rateLimit);

	watch(
		enabled,
		async (newValue) => {
			if (newValue) await fetch();
		},
		{
			immediate: true,
		},
	);

	return {
		rateLimit,
		refetch: fetch,
		...toRefs(state),
	};
};

export default useQuery;
